import { FC, MutableRefObject, Suspense, useMemo, useState } from 'react';
import { useMutation } from 'react-relay';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Users } from 'lucide-react';
import { Class } from '../../../models/Class';
import { Roles } from '../../../models/User';
import { addUsersToClass, removeUsersFromClass } from '../../../graphQLOperations/Classes';
import { ClassesAddUsersToClassMutation } from '../../../graphQLOperations/__generated__/ClassesAddUsersToClassMutation.graphql';
import { ClassesRemoveUsersFromClassMutation } from '../../../graphQLOperations/__generated__/ClassesRemoveUsersFromClassMutation.graphql';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import ClassWizardStudentsStep from './ClassWizardStudentsStep';
import ClassWizardNewStudentDialog from './ClassWizardNewStudentDialog';
import { WizardUser } from './wizardTypes';

interface Props {
  visible: boolean;
  onHide: () => void;
  selectedClass: Class;
  toast: MutableRefObject<Toast | null>;
  onSaved: () => void;
}

const ClassManageStudentsDialog: FC<Props> = ({ visible, onHide, selectedClass, toast, onSaved }) => {
  const originalStudents = useMemo<WizardUser[]>(
    () =>
      (selectedClass.users ?? [])
        .filter(user => user.isActive && user.roles?.some(role => role.name === Roles.STUDENT))
        .map(user => ({ id: user.id, firstName: user.firstName, lastName: user.lastName })),
    [selectedClass.users],
  );
  const [selected, setSelected] = useState<WizardUser[]>(originalStudents);
  const [newStudentVisible, setNewStudentVisible] = useState(false);

  const [addUsers, adding] = useMutation<ClassesAddUsersToClassMutation>(addUsersToClass);
  const [removeUsers, removing] = useMutation<ClassesRemoveUsersFromClassMutation>(removeUsersFromClass);
  const saving = adding || removing;

  const close = () => {
    setSelected(originalStudents);
    onHide();
  };

  const showError = () =>
    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not update students.' });

  const finish = () => {
    toast.current?.show({ severity: 'success', detail: 'Students updated.' });
    onSaved();
    onHide();
  };

  const save = () => {
    const toAdd = selected.filter(student => !originalStudents.some(o => o.id === student.id));
    const toRemove = originalStudents.filter(student => !selected.some(s => s.id === student.id));

    if (toAdd.length) {
      addUsers({
        variables: { input: { userIds: toAdd.map(s => s.id), classId: selectedClass.id } },
        onCompleted: () => {
          if (toRemove.length) {
            removeUsers({
              variables: { input: { userIds: toRemove.map(s => s.id), classId: selectedClass.id } },
              onCompleted: finish,
              onError: showError,
            });
          } else {
            finish();
          }
        },
        onError: showError,
      });
    } else if (toRemove.length) {
      removeUsers({
        variables: { input: { userIds: toRemove.map(s => s.id), classId: selectedClass.id } },
        onCompleted: finish,
        onError: showError,
      });
    } else {
      onHide();
    }
  };

  const header = (
    <div className="flex align-items-start gap-md">
      <span className="nc_wizard-header-icon">
        <Users size={17} aria-hidden />
      </span>
      <div className="flex flex-column">
        <h2 className="text-xl font-semibold line-height-1 m-0 tw-not-italic">Manage students</h2>
        <span className="text-color-secondary text-sm tw-leading-5 mt-1 tw-font-normal">
          Select from the list or add a new student. You can also remove or add additional students.
        </span>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={close} disabled={saving} />
      <Button label="Save" icon="nc_icon-nc_save icon-sm" rounded onClick={save} loading={saving} />
    </div>
  );

  return (
    <>
      <Dialog
        header={header}
        footer={footer}
        visible={visible}
        onHide={close}
        className="nc_class-wizard"
        style={{ width: '38.75rem', minHeight: '37rem' }}
        dismissableMask={false}
        ariaCloseIconLabel="Close"
      >
        <Suspense fallback={<ThreeDotLoader dark />}>
          <ClassWizardStudentsStep
            siteId={selectedClass.site?.id ?? ''}
            selected={selected}
            onChange={setSelected}
            onNewStudent={() => setNewStudentVisible(true)}
          />
        </Suspense>
      </Dialog>
      {newStudentVisible && (
        <ClassWizardNewStudentDialog
          visible
          onHide={() => setNewStudentVisible(false)}
          siteId={selectedClass.site?.id ?? ''}
          siteName={selectedClass.site?.name ?? ''}
          toast={toast}
          onCreated={student => {
            setSelected(prev => [...prev, student]);
            setNewStudentVisible(false);
          }}
        />
      )}
    </>
  );
};

export default ClassManageStudentsDialog;
